/* ExplorersLodge — landing page for the WildQuest community Discord.
 *
 * Content is fully i18n-driven (en/es/fr wired; falls back to English
 * for other locales) and shows a LIVE "X online now" widget proxied
 * through /api/lodge/discord-stats (60s cache).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MessagesSquare, Users, Sparkles, Compass, HeartHandshake,
  Bug, Bell, Camera, Leaf, Trophy, MapPin,
} from 'lucide-react';
import { useT, useI18n } from '../i18n/I18nContext';
import api from '../lib/api';
import FieldTeamWall from '../components/ar/FieldTeamWall';

const DISCORD_URL = 'https://discord.gg/NwjYhaWEq';

// Icon map for the 8 localized pillars (matched by index).
const PILLAR_ICONS = [Camera, Leaf, Compass, Trophy, Bell, Bug, HeartHandshake, Sparkles];

// Icon map for the 7 localized channels (matched by index).
const CHANNEL_ICONS = [Compass, Camera, Leaf, Trophy, MapPin, Bell, Sparkles];

export default function ExplorersLodge() {
  const nav = useNavigate();
  const t = useT();
  const { raw } = useI18n();
  const pillars  = Array.isArray(raw('lodge.pillars'))  ? raw('lodge.pillars')  : [];
  const rules    = Array.isArray(raw('lodge.rules'))    ? raw('lodge.rules')    : [];
  const channels = Array.isArray(raw('lodge.channels')) ? raw('lodge.channels') : [];

  return (
    <div className="pb-16" data-testid="explorers-lodge-page">
      <button
        type="button"
        onClick={() => nav(-1)}
        data-testid="lodge-back"
        className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-white mb-5"
        aria-label={t('common.back', 'Back')}
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t('common.back', 'Back')}
      </button>

      {/* Header */}
      <div className="flex items-start gap-3 mb-5">
        <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0">
          <MessagesSquare className="w-6 h-6 text-emerald-300" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">{t('lodge.eyebrow', 'WildQuest')}</p>
          <h1 className="font-serif text-3xl leading-tight">{t('lodge.title', "Explorer's Lodge")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('lodge.subtitle')}</p>
        </div>
      </div>

      {/* Hero card */}
      <div
        className="relative overflow-hidden rounded-3xl border border-emerald-400/40 bg-gradient-to-br from-[#0f2a1f] via-[#0a1a12] to-black p-5 sm:p-6 mb-6 shadow-2xl shadow-emerald-500/10"
        data-testid="lodge-hero"
      >
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full blur-3xl bg-emerald-500/25 pointer-events-none" />
        <div className="relative">
          <p className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> {t('lodge.hero.eyebrow')}
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl leading-tight text-white mb-3">
            {t('lodge.hero.headline')}
          </h2>
          <p className="text-[13px] leading-relaxed text-emerald-50/85 max-w-xl">
            {t('lodge.hero.body')}
          </p>

          {/* Live Discord widget */}
          <LiveDiscordWidget />

          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="lodge-discord-cta"
            aria-label={t('lodge.hero.cta')}
            className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#5865F2] hover:bg-[#4752c4] text-white text-sm font-semibold shadow-lg shadow-[#5865F2]/30 transition-colors"
          >
            <DiscordIcon />
            {t('lodge.hero.cta')}
          </a>
          <p className="mt-2 text-[10px] uppercase tracking-widest text-emerald-200/60">
            {t('lodge.hero.footnote')}
          </p>
        </div>
      </div>

      {/* Field-Team Wall — community AR photos */}
      <FieldTeamWall />

      {/* Pillars grid */}
      <section className="mb-8" data-testid="lodge-pillars">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          {t('lodge.pillarsTitle')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {pillars.map((p, i) => {
            const Icon = PILLAR_ICONS[i] || Leaf;
            return (
              <div
                key={i}
                data-testid={`lodge-pillar-${i}`}
                className="rounded-2xl border border-white/10 bg-black/30 p-3.5 flex gap-3"
              >
                <Icon className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5" strokeWidth={2} />
                <div className="min-w-0">
                  <p className="font-serif text-[15px] leading-tight text-white">{p.title}</p>
                  <p className="text-[12px] leading-snug text-white/70 mt-0.5">{p.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Guidelines */}
      <section className="mb-8" data-testid="lodge-rules">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          {t('lodge.rulesTitle')}
        </p>
        <div className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-2.5">
          {rules.map((rule, i) => (
            <div key={i} className="flex gap-2.5 text-[13px] leading-snug">
              <span className="text-emerald-400 font-mono text-[11px] mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
              <span className="text-white/85">{rule}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Channels */}
      <section className="mb-8" data-testid="lodge-channels">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          {t('lodge.channelsTitle')}
        </p>
        <div className="rounded-2xl border border-white/10 bg-black/30 divide-y divide-white/5">
          {channels.map((c, i) => {
            const Icon = CHANNEL_ICONS[i] || Compass;
            return (
              <div key={i} className="flex items-center gap-3 p-3">
                <Icon className="w-4 h-4 text-emerald-300 shrink-0" strokeWidth={2} />
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-sm text-white">{c.name}</p>
                  <p className="text-[11px] text-white/60 truncate">{c.note}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA */}
      <div className="text-center" data-testid="lodge-footer-cta">
        <p className="text-[13px] text-muted-foreground mb-3">
          {t('lodge.footerCtaLine')}
        </p>
        <a
          href={DISCORD_URL}
          target="_blank"
          rel="noreferrer noopener"
          data-testid="lodge-discord-cta-bottom"
          aria-label={t('lodge.footerCta')}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#5865F2] hover:bg-[#4752c4] text-white text-sm font-semibold shadow-lg shadow-[#5865F2]/30 transition-colors"
        >
          <DiscordIcon />
          {t('lodge.footerCta')}
        </a>
      </div>
    </div>
  );
}

/**
 * LiveDiscordWidget — polls the backend proxy every 60s and shows a
 * pulsing green dot + "X online now · Y members" line. Falls back
 * silently on network errors so a Discord outage never breaks the page.
 */
function LiveDiscordWidget() {
  const t = useT();
  const [state, setState] = useState({ loading: true, ok: false, online: null, members: null });

  useEffect(() => {
    let dead = false;
    const load = async () => {
      try {
        const { data } = await api.get('/lodge/discord-stats');
        if (dead) return;
        setState({
          loading: false,
          ok: !!data?.ok,
          online: data?.online ?? null,
          members: data?.members ?? null,
        });
      } catch {
        if (!dead) setState({ loading: false, ok: false, online: null, members: null });
      }
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => { dead = true; clearInterval(iv); };
  }, []);

  if (state.loading) {
    return (
      <div className="mt-4 inline-flex items-center gap-2 text-[11px] text-emerald-200/70" data-testid="lodge-live-loading">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/60 animate-pulse" />
        {t('lodge.live.loading', 'Reading the room…')}
      </div>
    );
  }

  if (!state.ok || state.online == null) {
    return (
      <div className="mt-4 inline-flex items-center gap-2 text-[11px] text-emerald-200/60" data-testid="lodge-live-unavailable">
        <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
        {t('lodge.live.unavailable', 'Live count unavailable — the Lodge is still open.')}
      </div>
    );
  }

  return (
    <div
      className="mt-4 inline-flex items-center gap-2 rounded-full bg-black/40 border border-emerald-400/40 px-3 py-1.5 text-[12px] text-white shadow-inner"
      data-testid="lodge-live-widget"
      aria-live="polite"
    >
      <span className="relative flex w-2 h-2">
        <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative w-2 h-2 rounded-full bg-emerald-400" />
      </span>
      <span className="font-mono tabular-nums text-emerald-200" data-testid="lodge-live-online">
        {state.online}
      </span>
      <span className="text-emerald-100/80">{t('lodge.live.online', 'online now')}</span>
      {state.members != null && (
        <>
          <span className="text-white/40">·</span>
          <span className="font-mono tabular-nums text-white/70" data-testid="lodge-live-members">{state.members}</span>
          <span className="text-white/60">{t('lodge.live.members', 'members')}</span>
        </>
      )}
    </div>
  );
}

function DiscordIcon() {
  return (
    <svg width="18" height="14" viewBox="0 0 71 55" fill="currentColor" aria-hidden>
      <path d="M60.104 4.552A58.396 58.396 0 0 0 45.865.001a.222.222 0 0 0-.235.111c-.618 1.1-1.302 2.535-1.78 3.66a53.876 53.876 0 0 0-16.246 0c-.478-1.148-1.187-2.56-1.81-3.66a.229.229 0 0 0-.235-.11 58.263 58.263 0 0 0-14.242 4.55.209.209 0 0 0-.096.083C1.578 18.352-.94 31.766.294 45.02a.244.244 0 0 0 .092.166 58.688 58.688 0 0 0 17.68 8.94.229.229 0 0 0 .249-.082 42.09 42.09 0 0 0 3.615-5.879.226.226 0 0 0-.124-.313 38.633 38.633 0 0 1-5.516-2.629.229.229 0 0 1-.023-.379c.372-.279.744-.569 1.099-.862a.225.225 0 0 1 .235-.031c11.575 5.286 24.104 5.286 35.544 0a.226.226 0 0 1 .238.029c.355.293.727.586 1.101.865a.229.229 0 0 1-.019.379 36.268 36.268 0 0 1-5.517 2.627.228.228 0 0 0-.121.315 47.208 47.208 0 0 0 3.615 5.877.226.226 0 0 0 .247.084 58.494 58.494 0 0 0 17.712-8.94.229.229 0 0 0 .092-.164c1.476-15.322-2.472-28.628-10.464-40.386a.19.19 0 0 0-.093-.084zM23.725 36.955c-3.487 0-6.36-3.201-6.36-7.132 0-3.93 2.818-7.132 6.36-7.132 3.57 0 6.416 3.229 6.36 7.132 0 3.931-2.818 7.132-6.36 7.132zm23.516 0c-3.487 0-6.36-3.201-6.36-7.132 0-3.93 2.817-7.132 6.36-7.132 3.569 0 6.416 3.229 6.36 7.132 0 3.931-2.791 7.132-6.36 7.132z"/>
    </svg>
  );
}
